import { useCallback, useMemo, useState } from "react";

/**
 * Per-page bulk selection. Selection is intentionally scoped to the rows the
 * user can currently see (current page/filter), so a bulk action never touches
 * records the user did not explicitly select.
 */
export function useBulkSelection<T extends { id: string }>(rows: T[]) {
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const someSelected = rows.length > 0 && rows.some((r) => selected.has(r.id));

  const toggle = useCallback((id: string) => {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected((s) => (s.size === rows.length && rows.length > 0 ? new Set() : new Set(rows.map((r) => r.id))));
  }, [rows]);

  const clear = useCallback(() => setSelected(new Set()), []);

  const selectedIds = useMemo(() => Array.from(selected), [selected]);

  return { selected, selectedIds, allSelected, someSelected, toggle, toggleAll, clear };
}